import { Alert, Box, Typography } from '@mui/material';
import { shortDateTime } from '../shared/dateTime';
import { viaDestinationLabel, type ViaAttempt, type ViaDeliveryRetry } from './viaWriteApi';

const KIND_LABEL: Record<string, string> = { MAIN: 'zlecenie', FORKLIFT: 'czynność wózkowa' };

/**
 * Automatyczne ponawianie dostarczenia pliku do VIA i ślad prób (paczka
 * p. Mariusza 21.10, BR-INT-06; przełącznik `viaDeliveryAutoRetry`,
 * S-21.10-12). Automat doręcza tylko ten sam, zatwierdzony plik; po
 * wyczerpaniu prób zlecenie trafia do kolejki „błąd zapisu” Administracji,
 * a „Ponów” zostaje dla człowieka.
 */
export const DeliveryRetryInfo = ({ retry }: { retry: ViaDeliveryRetry }) => {
  const { params } = retry;
  return (
    <Box sx={{ mb: 1 }} data-testid="via-delivery-retry">
      {retry.files.map((f) =>
        f.retry.state === 'pending' ? (
          <Alert key={f.kind} severity="info" sx={{ mb: 0.5 }} data-testid={`via-auto-retry-${f.kind}`}>
            Plik {KIND_LABEL[f.kind] ?? f.kind} nie dotarł — aplikacja ponawia dostarczenie sama (ten sam
            zatwierdzony plik): próba {f.retry.done + 1} z {f.retry.max}
            {f.retry.nextAt ? `, najbliższa ok. ${shortDateTime(f.retry.nextAt)}` : ''}.
          </Alert>
        ) : f.retry.state === 'exhausted' ? (
          <Alert key={f.kind} severity="warning" sx={{ mb: 0.5 }} data-testid={`via-auto-retry-${f.kind}`}>
            Automatyczne ponawianie pliku {KIND_LABEL[f.kind] ?? f.kind} zakończone
            {f.retry.exhaustedAt ? ` ${shortDateTime(f.retry.exhaustedAt)}` : ''} ({f.retry.done} z {f.retry.max}{' '}
            prób) — zlecenie jest w kolejce „błąd zapisu”; sprawdź przyczynę i ponów ręcznie.
          </Alert>
        ) : null,
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Ponawianie automatyczne: do {params.attempts} prób co {params.intervalMin} min (parametry „Zapis do VIA”).
      </Typography>
    </Box>
  );
};

/**
 * Ślad prób dostarczenia (poprawka obowiązkowa 21.10.2026, bez przełącznika):
 * każda próba osobno — zapis, eksport, ponowienie człowieka i automatu —
 * z czasem, wynikiem, celem, komunikatem i autorem. Poprzednia próba nie znika.
 */
export const AttemptTrail = ({ attempts }: { attempts: readonly ViaAttempt[] }) =>
  attempts.length === 0 ? null : (
    <Box sx={{ mb: 1 }} data-testid="via-delivery-attempts">
      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
        Próby dostarczenia
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {attempts.map((a, i) => (
          <li key={`${a.at}-${i}`}>
            <Typography variant="caption">
              {shortDateTime(a.at)} · {KIND_LABEL[a.kind] ?? a.kind} · {a.triggerLabel}
              {a.byName ? ` (${a.byName})` : ''} · {a.ok ? 'dostarczono' : 'błąd'}
              {a.destination ? ` · ${viaDestinationLabel(a.destination)}` : ''}
              {!a.ok && a.errorMessage ? ` — ${a.errorMessage}` : ''}
            </Typography>
          </li>
        ))}
      </Box>
    </Box>
  );
